<template>
  <v-container fluid>
    <ContentHeader
      header-icon="mdi-account-check-outline"
      header-title="My Attestations"
    ></ContentHeader>
    <FoldableCard
      non-foldable
      title="Compliance Overview"
      description="Select a vendor below to view, edit, or terminate"
    >
      <v-form @submit.prevent>
        <!-- Otherwise the initial search after a new/incognito tab would refresh the page usually-->
        <v-row>
          <v-col sm="4">
            <v-text-field
              class="mr-sm-4"
              label="TIN"
              v-model="searchFilters.tin"
              filled
              dense
            />
          </v-col>
          <v-col sm="4">
            <v-select
              filled
              dense
              label="Compliance Status"
              :items="complianceStatuses"
              clearable
              v-model="searchFilters.complianceStatus"
            >
            </v-select>
          </v-col>
          <v-col sm="4">
            <date-field
              v-model="searchFilters.nextCompletionDate"
              month-picker
              min-date="2020-01"
              :max-date="`${new Date().getFullYear() + 10}-01`"
              label="Next Due Date"
            ></date-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col sm="4">
            <v-select
              filled
              dense
              label="Form Status"
              :items="statuses.map(s => s.name)"
              clearable
              v-model="searchFilters.status"
            >
              <template v-slot:item="data">
                <v-badge
                  inline
                  dot
                  :color="getStatusColor(data.item)"
                ></v-badge>
                <span class="ml-1">{{ data.item }}</span>
              </template>
              <template v-slot:selection="data">
                <v-badge
                  inline
                  dot
                  :color="getStatusColor(data.item)"
                ></v-badge>
                <span class="ml-1">{{ data.item }}</span>
              </template>
            </v-select>
          </v-col>
          <v-col sm="4">
            <v-text-field
              class="mr-sm-4"
              label="Form Name"
              v-model="searchFilters.formName"
              filled
              dense
            />
          </v-col>
        </v-row>
        <v-card-actions class="pt-0 pb-8 justify-end">
          <v-btn
            text
            @click="reset()"
            class="px-8"
            :block="$vuetify.breakpoint.xsOnly"
          >
            <span>Reset</span>
          </v-btn>
          <v-btn
            type="submit"
            color="primary"
            @click="search()"
            class="px-8"
            :block="$vuetify.breakpoint.xsOnly"
          >
            Search
          </v-btn>
          <v-btn
            color="primary"
            @click="exportCSV()"
            :block="$vuetify.breakpoint.xsOnly"
            class="px-6"
          >
            Export as CSV
          </v-btn>
        </v-card-actions>
      </v-form>
      <div class="text-right mb-n10 mr-2">
        <v-menu v-model="changeColumnsMenu" :close-on-content-click="false">
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  fab
                  small
                  text
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                >
                  <v-icon color="colorUiGrey70">mdi-cog</v-icon>
                </v-btn>
              </template>
              <span>Show/Hide Columns</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item-group v-model="selectedHeaders" multiple>
              <v-list-item
                v-for="item in toggleableHeaders"
                :value="item"
                :key="`item-${item.value}`"
              >
                <template v-slot:default="{ active }">
                  <v-list-item-action>
                    <v-checkbox :input-value="active"></v-checkbox>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title v-text="item.text"></v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
      </div>
      <v-data-table
        class="main-table"
        :loading="dataTableLoading"
        disable-sort
        :headers="sortedSelectedHeaders"
        :items="items"
        :items-per-page="itemsPerPage"
        :page.sync="page"
        :server-items-length="itemsLength"
        hide-default-footer
      >
        <template v-slot:header.covgId="{ header }"></template>
        <template v-slot:item.status="{ value }">
          <v-badge inline dot :color="getStatusColor(value)"></v-badge>
          {{ value }}
        </template>
        <template v-slot:item.formName="{ value }">
          <div>
            <TextAndHovertext :text="value"></TextAndHovertext>
          </div>
        </template>

        <!--region Possibly multiple data per row-->
        <template v-slot:item.businessNames="{ value }">
          <div v-for="(name, index) in value" :key="'businessNames' + index">
            <TextAndHovertext :text="name"></TextAndHovertext>
          </div>
        </template>
        <template v-slot:item.vendorNumbers="{ value }">
          <div v-for="(number, index) in value" :key="'vendorNumbers' + index">
            <TextAndHovertext :text="number"></TextAndHovertext>
          </div>
        </template>
        <template v-slot:item.tins="{ value }">
          <div v-for="(tin, index) in value" :key="'tins' + index">
            {{ tin }}
          </div>
        </template>
        <template v-slot:item.complianceStatuses="{ value }">
          <div>
            {{ value[0] }}
          </div>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-chip v-if="value.length > 1" x-small>
                  View {{ value.length - 1 }} more
                </v-chip>
              </span>
            </template>
            <span>
              <div
                v-for="(complianceStatus, index) in value"
                :key="'complianceStatuses' + index"
              >
                <span v-if="index > 0">{{ complianceStatus }}</span>
              </div>
            </span>
          </v-tooltip>
        </template>
        <template v-slot:item.managedBy="{ value }">
          <div
            v-for="(managingClient, index) in value"
            :key="'managedByNames' + index"
          >
            <TextAndHovertext :text="managingClient"></TextAndHovertext>
          </div>
        </template>
        <!--endregion-->
        <template v-slot:item.covgId="{ value }">
          <v-btn
            :href="'/client/manage/vendors?covgId=' + value"
            height="32px"
            min-width="32px"
            class="tertiary"
            depressed
          >
            <v-icon color="primary">mdi-arrow-right</v-icon>
          </v-btn>
        </template>
      </v-data-table>
      <v-pagination v-model="page" :length="pageCount"></v-pagination>
    </FoldableCard>
  </v-container>
</template>

<script>
import ContentHeader from "@/views/ContentHeader";
import FoldableCard from "@/components/FoldableCard";
import TextAndHovertext from "@/components/TextAndHovertext";
import pdsVars from "@/pds/styles/pds-js-variables.scss";
import {
  checklistRequestsAPI,
  userPreferenceAPI,
  userPreferenceByKeyAPI
} from "@/services/api";
import { get, put, getCSV, getDownloadFilename } from "@/services/request";
import DateField from "@/components/DateField";
import { saveFile } from "@/services/download";
import hasNotifications from "@/mixins/has-notifications";
import { VENDOR_ATTESTATIONS_TABLE } from "@/services/constants";

const all_headers = [
  { text: "Vendor Number", value: "vendorNumbers" },
  { text: "Legal Business Name", value: "businessNames" },
  { text: "TIN", value: "tins", width: "120px" },
  { text: "Managed By", value: "managedBy" },
  {
    text: "Compliance Status",
    value: "complianceStatuses",
    width: "160px"
  },
  { text: "Form Name", value: "formName", width: "200px" },
  { text: "Form Status", value: "status", width: "160px" },
  {
    text: "Next Due Date",
    value: "nextCompletionDate",
    width: "120px"
  },
  {
    text: "Last Completion Date",
    value: "lastCompletionDate",
    width: "170px"
  },
  { value: "covgId", align: "right" }
];

const statuses = [
  { name: "Past Due", color: pdsVars.colorAlertRed },
  { name: "Pending", color: pdsVars.colorUiGrey50 },
  { name: "Not Started", color: pdsVars.colorAlertYellow },
  { name: "Due", color: pdsVars.colorAlertOrange },
  { name: "Complete", color: pdsVars.colorAlertGreen }
];

//Compliant + com.i2rd.vendorproof.vendor.NonComplianceReason
const complianceStatuses = [
  "Compliant",
  "Not Compliant - Excluded",
  "Not Compliant - Expired",
  "Not Compliant - Missing Forms",
  "Not Compliant - Not Enrolled",
  "Not Compliant - Owner Excluded",
  "Not Compliant - Waiver Rejected"
];

const statusColorMap = statuses.reduce((map, status) => {
  map[status.name] = status.color;
  return map;
}, {});

export default {
  mixins: [hasNotifications],
  name: "VendorAttestations",
  components: { DateField, TextAndHovertext, FoldableCard, ContentHeader },
  data: () => ({
    dataTableLoading: false,
    selectedHeaders: [],
    statuses,
    complianceStatuses,
    statusColorMap,
    items: [],
    itemsLength: 0,
    itemsPerPage: 25,
    page: 1,
    pageCount: 1,
    searchFilters: {},
    changeColumnsMenu: false
  }),
  computed: {
    pageIndex() {
      return this.page - 1; //vuetify uses 1-indexed page, server uses 0-indexed
    },
    toggleableHeaders() {
      return all_headers.filter(value => value.text);
    },
    sortedSelectedHeaders() {
      let result = [...this.selectedHeaders];
      result.sort(function(a, b) {
        return a.order - b.order;
      });
      return result;
    }
  },
  watch: {
    page() {
      this.loadTableData();
    },
    changeColumnsMenu(openMenu) {
      if (openMenu === false) {
        this.saveColumnPreferences();
      }
    }
  },
  created() {
    //region Copy and assign header order
    this.selectedHeaders = all_headers;
    let order = 0;
    this.selectedHeaders.forEach(item => (item.order = order++));
    //endregion

    get(userPreferenceByKeyAPI.getUrl({ key: VENDOR_ATTESTATIONS_TABLE })).then(
      response => {
        if (response && response.data) {
          let preferences = response.data;
          this.selectedHeaders = this.selectedHeaders.filter(header => {
            let preference = preferences.find(
              pref => pref.value === header.value
            );
            return !preference || preference.visible;
          });
        }
      }
    );

    this.loadTableData();
  },
  methods: {
    saveColumnPreferences() {
      let headerPreferences = [...all_headers];
      headerPreferences.forEach(pref => {
        let selectedHeader = this.selectedHeaders.find(
          header => header.value === pref.value
        );
        pref.visible = !!selectedHeader;
      });
      put(userPreferenceAPI.getUrl(), {
        key: VENDOR_ATTESTATIONS_TABLE,
        value: JSON.stringify(headerPreferences, null, 2)
      });
    },
    getFilters() {
      const {
        tin,
        status,
        nextCompletionDate,
        complianceStatus,
        formName
      } = this.searchFilters;
      return {
        ...(nextCompletionDate && {
          nextCompletionDate: `${nextCompletionDate}-01`
        }),
        ...(tin &&
          tin.trim().length && {
            tin: tin.trim().replaceAll("-", "")
          }),
        ...(status && {
          status: status.toUpperCase().replace(" ", "_")
        }),
        ...(complianceStatus && {
          complianceStatus: complianceStatus
            .toUpperCase()
            .replace("NOT COMPLIANT - EXPIRED", "SUBSCRIPTION_EXPIRED")
            .replace("NOT COMPLIANT - ", "")
            .replaceAll(" ", "_")
        }),
        ...(formName &&
          formName.trim().length && {
            formName: formName.trim()
          })
      };
    },
    loadTableData() {
      this.dataTableLoading = true;
      get(checklistRequestsAPI, {
        page: this.pageIndex,
        size: this.itemsPerPage,
        ...this.getFilters()
      }).then(response => {
        this.itemsLength = response.data.size;
        this.pageCount = Math.ceil(response.data.total / this.itemsLength);
        this.items = response.data.content;
        this.dataTableLoading = false;
      });
    },
    getStatusColor(status) {
      return this.statusColorMap[status] || pdsVars.colorUiGrey50;
    },
    reset() {
      this.searchFilters = {};
      this.search();
    },
    search() {
      this.page = 1;
      this.loadTableData();
    },
    async exportCSV() {
      //Wait for column prefs to save
      if (this.changeColumnsMenu) {
        this.changeColumnsMenu = false;
        await new Promise(r => setTimeout(r, 500));
      }

      getCSV(checklistRequestsAPI, this.getFilters())
        .then(response => {
          saveFile(response.data, getDownloadFilename(response));
        })
        .catch(e => {
          const errorMsg = "Error downloading CSV";
          console.error(errorMsg, e);
          this.showMessage({
            mode: "error",
            text: errorMsg
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep td {
  max-width: 0; //This makes the table responsive and work with text-truncate
}

.v-list {
  min-width: 30ch; //Needed for IE11, see https://github.com/vuetifyjs/vuetify/issues/8338#issue-477719361
}
</style>
